import type { StyleSpecification } from "maplibre-gl";

export interface ResolvedAddress {
  street: string;
  neighborhood?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  label: string;
  lat: number;
  lng: number;
}

export interface PlaceSuggestion {
  placeId: string;
  label: string;
  secondaryText: string;
}

const ENV_TOMTOM_API_KEY = import.meta.env.VITE_TOMTOM_API_KEY as string | undefined;
export const TOMTOM_API_KEY =
  ENV_TOMTOM_API_KEY?.trim() || "PUi7POc081z8FTAwVSHnDoJzswRc12hE";

const ENV_AWS_REGION = import.meta.env.VITE_AWS_REGION as string | undefined;
export const AWS_REGION = ENV_AWS_REGION?.trim() || "eu-west-1";

const ENV_AWS_PLACE_INDEX = import.meta.env.VITE_AWS_PLACE_INDEX_NAME as string | undefined;
export const AWS_PLACE_INDEX = ENV_AWS_PLACE_INDEX?.trim() || "TappyaPlaceIndex";

const ENV_AWS_LOCATION_API_KEY = import.meta.env.VITE_AWS_LOCATION_API_KEY as string | undefined;
export const AWS_LOCATION_API_KEY =
  ENV_AWS_LOCATION_API_KEY?.trim() ||
  "v1.public.eyJqdGkiOiJlMDU1NmZkMi02YTIzLTQ1MzQtODIzZi01NmFmMzQ3Mjg5NDMifQCYgxh5F5fIzz_XV6xK6WJKpUZHoCKbk-SEPxos8xBTM7SptOECUmbQ9XLlebTFviECHZ7Pxiz7572CwX26Ejc0HsuS3F8qXyScIt2wV7yhoO4852xY78bMR0gbiwda28ecpabIUkvZ84d0C0zUn8VcjIS0ymiZOWWh1gpVO1TT_6Ley-xnXqmkVFKkraex_I6GKAdy7yT-KqkjL1bfdUl7Zt0MeeLAbKWLl1l9gMW_fwQJ7iq6ee4V31u7m040ecd9XLr9oK2sq-No94KQO0JK-brXn6M0x-aVpoTwNCAd1tlu398I3jWtRrkor7TRnCKYUKpcxY08Mw8Sd8gOuyA.ZGQzZDY2OGQtMWQxMy00ZTEwLWIyZGUtOGVjYzUzMjU3OGE4";

const TOMTOM_RASTER_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    tomtom: {
      type: "raster",
      tiles: [
        `https://api.tomtom.com/map/1/tile/basic/main/{z}/{x}/{y}.png?key=${TOMTOM_API_KEY}`,
      ],
      tileSize: 256,
      attribution: "© TomTom",
    },
  },
  layers: [
    {
      id: "tomtom-raster",
      type: "raster",
      source: "tomtom",
      minzoom: 0,
      maxzoom: 22,
    },
  ],
};

export const DEFAULT_MAP_STYLE: string | StyleSpecification = TOMTOM_RASTER_STYLE;

export const DEFAULT_CENTER: [number, number] = [31.2357, 30.0444];
export const DEFAULT_ZOOM = 13;

const COUNTRY_ALIASES: Array<[string, string]> = [
  ["egypt", "EG"],
  ["مصر", "EG"],
  ["saudi arabia", "SA"],
  ["kingdom of saudi arabia", "SA"],
  ["ksa", "SA"],
  ["السعودية", "SA"],
  ["المملكة العربية السعودية", "SA"],
  ["united arab emirates", "AE"],
  ["uae", "AE"],
  ["الإمارات", "AE"],
  ["الامارات", "AE"],
  ["kuwait", "KW"],
  ["الكويت", "KW"],
  ["qatar", "QA"],
  ["قطر", "QA"],
  ["bahrain", "BH"],
  ["البحرين", "BH"],
  ["oman", "OM"],
  ["عمان", "OM"],
  ["jordan", "JO"],
  ["الأردن", "JO"],
  ["الاردن", "JO"],
  ["lebanon", "LB"],
  ["لبنان", "LB"],
  ["iraq", "IQ"],
  ["العراق", "IQ"],
  ["morocco", "MA"],
  ["المغرب", "MA"],
  ["tunisia", "TN"],
  ["تونس", "TN"],
  ["algeria", "DZ"],
  ["الجزائر", "DZ"],
  ["united states", "US"],
  ["usa", "US"],
  ["us", "US"],
];

const ISO3_TO_ISO2: Record<string, string> = {
  ARE: "AE",
  BHR: "BH",
  DZA: "DZ",
  EGY: "EG",
  IRQ: "IQ",
  JOR: "JO",
  KWT: "KW",
  LBN: "LB",
  MAR: "MA",
  OMN: "OM",
  QAT: "QA",
  SAU: "SA",
  TUN: "TN",
  USA: "US",
};

function cleanCountryText(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u064B-\u065F\u0670\u0640]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

export function normalizeCountryCode(value: string | null | undefined): string {
  const raw = value?.trim();
  if (!raw) return "";
  const upper = raw.toUpperCase();
  if (/^[A-Z]{2}$/.test(upper)) return upper;
  if (ISO3_TO_ISO2[upper]) return ISO3_TO_ISO2[upper];

  const cleaned = cleanCountryText(raw);
  for (const [alias, code] of COUNTRY_ALIASES) {
    if (cleaned === cleanCountryText(alias) || cleaned.includes(cleanCountryText(alias))) {
      return code;
    }
  }
  return "";
}

/**
 * Resolve the first usable ISO 3166-1 alpha-2 country code from an ordered list
 * of candidates, falling back to `fallback` (default "EG", the app's home
 * country). Guarantees a non-empty, valid 2-letter code so address writes never
 * fail the backend's `country` validation (min_length=2) — an empty country
 * previously 500'd the create-address call and blocked checkout.
 */
export function resolveCountryCode(
  candidates: Array<string | null | undefined>,
  fallback = "EG",
): string {
  for (const candidate of candidates) {
    const code = normalizeCountryCode(candidate);
    if (code) return code;
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// Geocoding providers.
//  - Reverse geocoding (pin -> address): Google Maps client-side Geocoder (see
//    reverseGeocodeGoogle / reverseGeocode below). We moved off TomTom's
//    reverseGeocode after its Search API started returning 403 "insufficient
//    funds". The Google Maps JS SDK is already loaded in index.html and the
//    Online Ordering key is authorized for the Geocoding API.
//  - Forward geocoding / search: still TomTom (forwardGeocodeTomTom) plus Google
//    Places autocomplete (autocompleteAddress/resolvePlaceId) for result quality.
//  - Map tiles: TomTom raster (TOMTOM_RASTER_STYLE above) — a separate quota
//    from the Search API, so tiles keep rendering even while Search is 403ing.
// ---------------------------------------------------------------------------

type TomTomAddress = {
  freeformAddress?: string;
  streetNumber?: string;
  streetName?: string;
  municipality?: string;
  localName?: string;
  countrySecondarySubdivision?: string;
  countrySubdivision?: string;
  postalCode?: string;
  country?: string;
  countryCode?: string;
};

type TomTomSearchResult = {
  position?: {
    lat?: number;
    lon?: number;
  };
  address?: TomTomAddress;
};

type TomTomSearchResponse = {
  results?: TomTomSearchResult[];
};

function addressToLabel(address: TomTomAddress, street: string): string {
  return (
    address.freeformAddress ||
    [street, address.municipality, address.countrySubdivision, address.postalCode, address.country]
      .filter(Boolean)
      .join(", ")
  );
}

function pickTomTomAddress(
  address: TomTomAddress | undefined,
  lat: number,
  lng: number,
): ResolvedAddress | null {
  if (!address || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const street = [address.streetNumber, address.streetName].filter(Boolean).join(" ").trim();
  const label = addressToLabel(address, street);
  return {
    street: street || label.split(",")[0]?.trim() || "",
    city:
      address.municipality ||
      address.localName ||
      address.countrySecondarySubdivision ||
      "",
    state: address.countrySubdivision || "",
    postalCode: address.postalCode || "",
    country: normalizeCountryCode(address.countryCode || address.country),
    label,
    lat,
    lng,
  };
}

async function forwardGeocodeTomTom(
  text: string,
  opts: { biasPosition?: [number, number]; maxResults?: number } = {},
): Promise<ResolvedAddress[]> {
  if (!TOMTOM_API_KEY) return [];
  try {
    const url = new URL(
      `https://api.tomtom.com/search/2/search/${encodeURIComponent(text.trim())}.json`,
    );
    url.searchParams.set("key", TOMTOM_API_KEY);
    url.searchParams.set("limit", String(opts.maxResults ?? 5));
    url.searchParams.set("typeahead", "true");
    if (opts.biasPosition) {
      url.searchParams.set("lon", String(opts.biasPosition[0]));
      url.searchParams.set("lat", String(opts.biasPosition[1]));
    }

    const res = await fetch(url.toString());
    if (!res.ok) throw new Error(`TomTom search returned ${res.status}`);
    const data = (await res.json()) as TomTomSearchResponse;
    const out: ResolvedAddress[] = [];
    for (const result of data.results ?? []) {
      const lat = result.position?.lat;
      const lng = result.position?.lon;
      if (typeof lat !== "number" || typeof lng !== "number") continue;
      const place = pickTomTomAddress(result.address, lat, lng);
      if (place) out.push(place);
    }
    return out;
  } catch (err) {
    console.warn("[location] TomTom forwardGeocode failed", err);
    return [];
  }
}

// ---------------------------------------------------------------------------
// AWS Location Service: reverse geocoding (pin -> address)
// ---------------------------------------------------------------------------

type AwsPlaceGeometry = {
  Point?: [number, number]; // [lng, lat]
};

type AwsPlace = {
  Label?: string;
  AddressNumber?: string;
  Street?: string;
  Neighborhood?: string;
  Municipality?: string;
  SubRegion?: string;
  Region?: string;
  Country?: string;
  PostalCode?: string;
  Geometry?: AwsPlaceGeometry;
  Categories?: string[];
};

type AwsPositionSearchResponse = {
  Summary?: {
    Position?: [number, number];
  };
  Results?: Array<{
    Place?: AwsPlace;
    Distance?: number;
    PlaceId?: string;
  }>;
};

function pickAwsPlace(
  place: AwsPlace | undefined,
  fallbackLat: number,
  fallbackLng: number,
): ResolvedAddress | null {
  if (!place) return null;
  const point = place.Geometry?.Point;
  const lat = point && typeof point[1] === "number" ? point[1] : fallbackLat;
  const lng = point && typeof point[0] === "number" ? point[0] : fallbackLng;

  const street = [place.AddressNumber, place.Street].filter(Boolean).join(" ").trim();
  // Strip BiDi isolation marks if present in returned string
  const label = (place.Label || "").replace(/[\u2066\u2067\u2068\u2069]/g, "").trim();
  const labelParts = label.split(/[,\u060C]/).map((p) => p.trim()).filter(Boolean);
  const neighborhood = place.Neighborhood || "";
  const city =
    place.Municipality ||
    place.SubRegion ||
    place.Region ||
    neighborhood ||
    "";
  const state = place.Region || "";
  const postalCode = place.PostalCode || "";
  const country = normalizeCountryCode(place.Country);

  return {
    street: street || labelParts[0] || "",
    neighborhood,
    city,
    state,
    postalCode,
    country,
    label: label || [street, neighborhood, city, state, country].filter(Boolean).join(", "),
    lat,
    lng,
  };
}

// In-memory cache for reverse geocoded positions (~11m grid resolution)
const reverseGeocodeCache = new Map<string, ResolvedAddress>();
const MAX_CACHE_SIZE = 100;

function getCacheKey(lat: number, lng: number): string {
  return `${lat.toFixed(4)},${lng.toFixed(4)}`;
}

export function clearReverseGeocodeCache(): void {
  reverseGeocodeCache.clear();
}

async function reverseGeocodeAws(
  lat: number,
  lng: number,
  language = "ar",
): Promise<ResolvedAddress | null> {
  if (!AWS_LOCATION_API_KEY || !AWS_PLACE_INDEX || !AWS_REGION) return null;
  try {
    const url = `https://places.geo.${AWS_REGION}.amazonaws.com/places/v0/indexes/${AWS_PLACE_INDEX}/search/position?key=${AWS_LOCATION_API_KEY}`;
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        Position: [lng, lat],
        MaxResults: 3,
        Language: language,
      }),
    });

    if (!res.ok) {
      console.warn(`[location] AWS reverseGeocode failed: HTTP ${res.status}`);
      return null;
    }

    const data = (await res.json()) as AwsPositionSearchResponse;
    const results = data.Results || [];
    if (results.length === 0) return null;

    // Prefer AddressType or StreetType if available, otherwise first result
    const best =
      results.find((r) => {
        const cats = r.Place?.Categories || [];
        return cats.includes("AddressType") || cats.includes("StreetType");
      }) || results[0];

    return pickAwsPlace(best?.Place, lat, lng);
  } catch (err) {
    console.warn("[location] AWS reverseGeocode error", err);
    return null;
  }
}

export async function reverseGeocode(
  lat: number,
  lng: number,
): Promise<ResolvedAddress | null> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const cacheKey = getCacheKey(lat, lng);
  if (reverseGeocodeCache.has(cacheKey)) {
    return reverseGeocodeCache.get(cacheKey)!;
  }

  // 1. Primary: AWS Location Service
  const awsResult = await reverseGeocodeAws(lat, lng);
  if (awsResult) {
    if (reverseGeocodeCache.size >= MAX_CACHE_SIZE) {
      const firstKey = reverseGeocodeCache.keys().next().value;
      if (firstKey) reverseGeocodeCache.delete(firstKey);
    }
    reverseGeocodeCache.set(cacheKey, awsResult);
    return awsResult;
  }

  // 2. Fallback: Google Geocoding (if AWS is unavailable)
  const googleResult = await reverseGeocodeGoogle(lat, lng);
  if (googleResult) {
    if (reverseGeocodeCache.size >= MAX_CACHE_SIZE) {
      const firstKey = reverseGeocodeCache.keys().next().value;
      if (firstKey) reverseGeocodeCache.delete(firstKey);
    }
    reverseGeocodeCache.set(cacheKey, googleResult);
    return googleResult;
  }

  return null;
}

export async function forwardGeocode(
  text: string,
  opts: { biasPosition?: [number, number]; maxResults?: number } = {},
): Promise<ResolvedAddress[]> {
  if (!text || text.trim().length < 2) return [];
  return forwardGeocodeTomTom(text, opts);
}

// ---------------------------------------------------------------------------
// Google Maps: reverse geocoding (pin -> address) + Places search/autocomplete.
// ---------------------------------------------------------------------------

/**
 * Parse Google address components (from Geocoder or PlacesService) into our
 * ResolvedAddress. Shared by reverse geocoding and place-detail resolution so
 * both produce identical shapes (and normalize country the same way).
 */
function googleComponentsToResolved(
  components: google.maps.GeocoderAddressComponent[],
  formattedAddress: string,
  lat: number,
  lng: number,
): ResolvedAddress {
  let streetNumber = "";
  let route = "";
  let neighborhood = "";
  let city = "";
  let adminAreaLevel2 = "";
  let state = "";
  let postalCode = "";
  let country = "";

  for (const component of components || []) {
    const types = component.types;
    if (types.includes("street_number")) streetNumber = component.long_name;
    else if (types.includes("route")) route = component.long_name;
    else if (
      types.includes("neighborhood") ||
      types.includes("sublocality_level_1")
    )
      neighborhood = component.long_name;
    else if (
      types.includes("locality") ||
      types.includes("postal_town") ||
      types.includes("sublocality")
    )
      city = component.long_name;
    else if (types.includes("administrative_area_level_2"))
      adminAreaLevel2 = component.long_name;
    else if (types.includes("administrative_area_level_1"))
      state = component.long_name || component.short_name;
    else if (types.includes("postal_code")) postalCode = component.long_name;
    else if (types.includes("country"))
      country = normalizeCountryCode(component.short_name);
  }

  const street = [streetNumber, route].filter(Boolean).join(" ").trim();
  let label = formattedAddress || "";
  const parts = label.split(/[,\u060C]/).map((p) => p.trim()).filter(Boolean);
  if (parts.length > 2) {
    label = parts.slice(0, parts.length - 1).join(", ");
  }

  const resolvedCity =
    city ||
    adminAreaLevel2 ||
    neighborhood ||
    state ||
    "";

  return {
    street: street || parts[0] || "",
    neighborhood,
    city: resolvedCity,
    state,
    postalCode,
    country,
    label,
    lat,
    lng,
  };
}

/**
 * Reverse geocode a coordinate to an address using the Google Maps JS Geocoder.
 * Returns null if the SDK isn't loaded or Google can't resolve the point (the
 * caller then falls back to the raw coordinates).
 */
async function reverseGeocodeGoogle(
  lat: number,
  lng: number,
): Promise<ResolvedAddress | null> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (!window.google?.maps?.Geocoder) return null;
  return new Promise((resolve) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        const r = results[0];
        const loc = r.geometry?.location;
        resolve(
          googleComponentsToResolved(
            r.address_components || [],
            r.formatted_address || "",
            loc ? loc.lat() : lat,
            loc ? loc.lng() : lng,
          ),
        );
      } else {
        if (status !== "ZERO_RESULTS") {
          console.warn("[location] Google reverseGeocode failed:", status);
        }
        resolve(null);
      }
    });
  });
}

export async function autocompleteAddress(
  text: string,
  opts: { biasPosition?: [number, number]; maxResults?: number; country?: string } = {},
): Promise<PlaceSuggestion[]> {
  if (!text || text.trim().length < 2) return [];
  return new Promise((resolve) => {
    if (!window.google?.maps?.places?.AutocompleteService) {
      return resolve([]);
    }
    const service = new window.google.maps.places.AutocompleteService();

    let locationBias: google.maps.LatLng | undefined;
    if (opts.biasPosition) {
       locationBias = new google.maps.LatLng(opts.biasPosition[1], opts.biasPosition[0]);
    }

    const country = normalizeCountryCode(opts.country);

    service.getPlacePredictions(
      {
        input: text,
        // `location`/`radius` are deprecated on AutocompletionRequest and fall
        // back to IP-based bias; `locationBias` (CircleLiteral) is the
        // supported way to bias predictions to the user's position.
        ...(locationBias
          ? { locationBias: { center: locationBias, radius: 50000 } }
          : {}),
        componentRestrictions: country ? { country } : undefined,
      },
      (predictions, status) => {
        if (status === "OK" && predictions) {
          const out = predictions.slice(0, opts.maxResults ?? 5).map(p => ({
            placeId: p.place_id,
            label: p.structured_formatting.main_text,
            secondaryText: p.structured_formatting.secondary_text,
          }));
          resolve(out);
        } else {
          if (status !== "ZERO_RESULTS") {
             console.warn("[location] Google autocomplete failed:", status);
          }
          resolve([]);
        }
      }
    );
  });
}

export async function resolvePlaceId(placeId: string): Promise<ResolvedAddress | null> {
    return new Promise((resolve) => {
        if (!window.google?.maps?.places?.PlacesService) {
            return resolve(null);
        }
        
        // We need a dummy div to initialize PlacesService
        const dummyDiv = document.createElement("div");
        const service = new window.google.maps.places.PlacesService(dummyDiv);
        
        service.getDetails({ placeId, fields: ["geometry", "address_components", "formatted_address"] }, (place, status) => {
            if (status === "OK" && place && place.geometry && place.geometry.location) {
                resolve(
                    googleComponentsToResolved(
                        place.address_components || [],
                        place.formatted_address || "",
                        place.geometry.location.lat(),
                        place.geometry.location.lng(),
                    ),
                );
            } else {
                console.error("Places API getDetails failed:", status);
                resolve(null);
            }
        });
    });
}

export async function geocodeAddress(parts: {
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}): Promise<{ latitude: number; longitude: number } | null> {
  const text = [parts.street, parts.city, parts.state, parts.postalCode, parts.country]
    .filter((p) => p && p.trim())
    .join(", ");
  if (!text) return null;

  const results = await forwardGeocodeTomTom(text, { maxResults: 1 });
  const top = results[0];
  if (!top) return null;
  return { latitude: top.lat, longitude: top.lng };
}
